import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  ANNOUNCEMENTS_COLLECTION,
  dismissalKey,
  isWithinScheduleWindow,
  resolveAnnouncement,
} from '../constants/announcements';

function readDismissedVersion(id) {
  try {
    const raw = window.localStorage.getItem(dismissalKey(id));
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeDismissedVersion(id, version) {
  try {
    window.localStorage.setItem(dismissalKey(id), String(version));
  } catch {
    // localStorage can throw in private mode / disabled storage. Silent fail
    // is fine — the user just sees the banner again next page load.
  }
}

/**
 * Subscribes to the two announcement queries that match the firestore.rules
 * predicate exactly (mode==='all' OR uid in target.uids), filters by enabled
 * + schedule window + dismissal, and returns the highest-priority match.
 *
 * Returns:
 *   { announcement, dismiss(id, version) }
 *
 * The hook is auth-aware: when there is no signed-in user, it resolves
 * to `null` (announcements require an authenticated UID for targeting).
 */
export default function useActiveAnnouncement() {
  const [uid, setUid] = useState(() => auth.currentUser?.uid || null);
  const [allTargeted, setAllTargeted] = useState([]);
  const [userTargeted, setUserTargeted] = useState([]);
  const [dismissedVersionsById, setDismissedVersionsById] = useState({});
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      setUid(u?.uid || null);
    });
    return () => unsub();
  }, []);

  // Subscribe to "all"-mode announcements. Anyone signed in can read these
  // (the rule only requires enabled === true && target.mode === 'all').
  useEffect(() => {
    if (!uid) {
      setAllTargeted([]);
      return undefined;
    }
    const q1 = query(
      collection(db, ANNOUNCEMENTS_COLLECTION),
      where('enabled', '==', true),
      where('target.mode', '==', 'all'),
    );
    const unsub = onSnapshot(
      q1,
      (snap) => {
        setAllTargeted(snap.docs.map((d) => resolveAnnouncement(d.id, d.data())));
      },
      (err) => {
        console.warn('[useActiveAnnouncement] all-mode listener:', err?.message || err);
        setAllTargeted([]);
      },
    );
    return () => unsub();
  }, [uid]);

  // Subscribe to "selected"-mode announcements where this uid is in target.uids.
  useEffect(() => {
    if (!uid) {
      setUserTargeted([]);
      return undefined;
    }
    const q2 = query(
      collection(db, ANNOUNCEMENTS_COLLECTION),
      where('enabled', '==', true),
      where('target.uids', 'array-contains', uid),
    );
    const unsub = onSnapshot(
      q2,
      (snap) => {
        setUserTargeted(snap.docs.map((d) => resolveAnnouncement(d.id, d.data())));
      },
      (err) => {
        console.warn('[useActiveAnnouncement] selected-mode listener:', err?.message || err);
        setUserTargeted([]);
      },
    );
    return () => unsub();
  }, [uid]);

  // Re-evaluate every minute so schedule windows tick over without a refresh.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const active = useMemo(() => {
    if (!uid) return null;
    const seen = new Set();
    const merged = [];
    for (const a of [...allTargeted, ...userTargeted]) {
      if (!a || seen.has(a.id)) continue;
      seen.add(a.id);
      if (!a.enabled) continue;
      if (!isWithinScheduleWindow(a)) continue;
      const dismissedVersion = dismissedVersionsById[a.id] ?? readDismissedVersion(a.id);
      if (a.dismissible && a.version <= dismissedVersion) continue;
      merged.push(a);
    }
    if (merged.length === 0) return null;
    merged.sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority;
      const aT = a.updatedAt ? Date.parse(a.updatedAt) : 0;
      const bT = b.updatedAt ? Date.parse(b.updatedAt) : 0;
      return bT - aT;
    });
    return merged[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, allTargeted, userTargeted, dismissedVersionsById, tick]);

  const dismiss = (id, version) => {
    if (!id || !Number.isFinite(version)) return;
    writeDismissedVersion(id, version);
    setDismissedVersionsById((prev) => ({ ...prev, [id]: version }));
  };

  return { announcement: active, dismiss };
}
